import { Frequency, Weekday } from "../schema/schedule-form-schema";
import { FrequencyType, NotificationSchedule } from "../types";

/**
 * A UI oferece frequências de calendário; a API só entende
 * `INTERVAL` (a cada N dias) ou `CRON` (expressão de 5 campos).
 * Este módulo traduz nos dois sentidos.
 */

/** Ordem = índice do dia no cron (0 = domingo). */
const CRON_WEEKDAYS: Record<Weekday, string> = {
  monday: "MON",
  tuesday: "TUE",
  wednesday: "WED",
  thursday: "THU",
  friday: "FRI",
};

const CRON_WEEKDAY_TO_UI = Object.fromEntries(
  Object.entries(CRON_WEEKDAYS).map(([ui, cron]) => [cron, ui as Weekday]),
) as Record<string, Weekday>;

type FrequencyFields = Pick<
  NotificationSchedule,
  "frequency_type" | "interval_days" | "cron_expr"
>;

/**
 * Converte a frequência escolhida na UI para os campos aceitos pela API.
 *
 * - `daily`     → INTERVAL a cada 1 dia
 * - `biweekly`  → INTERVAL a cada 14 dias
 * - `weekly`    → CRON nos dias marcados
 * - `monthly`   → CRON no dia 1
 * - `quarterly` → CRON no dia 1 de jan/abr/jul/out
 */
export function toApiFrequency(
  frequency: Frequency,
  weekdays: Weekday[],
  sendTime: string,
): FrequencyFields {
  const [hour, minute] = sendTime.split(":").map(Number);

  switch (frequency) {
    case "daily":
      return {
        frequency_type: "INTERVAL",
        interval_days: 1,
        cron_expr: null,
      };
    case "biweekly":
      return {
        frequency_type: "INTERVAL",
        interval_days: 14,
        cron_expr: null,
      };
    case "weekly": {
      const days = weekdays.map((day) => CRON_WEEKDAYS[day]).join(",");
      return {
        frequency_type: "CRON",
        interval_days: null,
        cron_expr: `${minute} ${hour} * * ${days}`,
      };
    }
    case "monthly":
      return {
        frequency_type: "CRON",
        interval_days: null,
        cron_expr: `${minute} ${hour} 1 * *`,
      };
    case "quarterly":
      return {
        frequency_type: "CRON",
        interval_days: null,
        cron_expr: `${minute} ${hour} 1 1,4,7,10 *`,
      };
  }
}

/**
 * Caminho inverso, para popular o form ao editar. Como o mapeamento não é
 * bijetivo (qualquer cron cabe em `CRON`), reconhecemos os formatos que nós
 * mesmos geramos e caímos em `weekly` quando há dias da semana.
 */
export function fromApiFrequency(schedule: FrequencyFields): {
  frequency: Frequency;
  weekdays: Weekday[];
} {
  if (schedule.frequency_type === "INTERVAL") {
    return {
      frequency: schedule.interval_days === 14 ? "biweekly" : "daily",
      weekdays: [],
    };
  }

  const cron = schedule.cron_expr ?? "";
  const fields = cron.trim().split(/\s+/);
  // "m h dom mon dow"
  const [, , dayOfMonth, month, dayOfWeek] = fields;

  if (dayOfWeek && dayOfWeek !== "*") {
    return {
      frequency: "weekly",
      weekdays: dayOfWeek
        .split(",")
        .map((day) => CRON_WEEKDAY_TO_UI[day.trim().toUpperCase()])
        .filter((day): day is Weekday => Boolean(day)),
    };
  }

  if (dayOfMonth === "1" && month && month !== "*") {
    return { frequency: "quarterly", weekdays: [] };
  }

  return { frequency: "monthly", weekdays: [] };
}
